import { CATEGORY_SETUP, useEditor, type LightCategory } from '../store/useEditor'
import {
  IconBow,
  IconBush,
  IconCluster,
  IconColumn,
  IconRoofline,
  IconSwag,
  IconTree,
  IconWreath,
} from './icons'

/**
 * WHAT YOU CAN PUT ON A HOUSE, AS A LIST YOU CAN SEE.
 *
 * The design package's editor prototype has this as a permanent panel down the
 * left hand side, headed Lighting, with the nine things grouped under three
 * headings: what is on the building, what is in the garden, and what goes on
 * last. We had the same nine hidden behind Add in the toolbar, which means a
 * person opening the editor for the first time cannot see what the product
 * does without pressing something.
 *
 * THE GROUPS ARE THE REFERENCE'S, and they are not arbitrary: they are the
 * order somebody actually works in. Architecture is drawn from the ground with
 * a pole, landscape is walked, and the finishing touches go on at the end when
 * the ladder is already down.
 */
const GROUPS: { label: string; ids: LightCategory[] }[] = [
  { label: 'Architecture', ids: ['roofline', 'garland', 'columns'] },
  { label: 'Landscape', ids: ['palms', 'trees', 'other'] },
  { label: 'Finishing touches', ids: ['wreaths', 'clusters', 'accessories'] },
]

const ICONS: Partial<Record<LightCategory, (p: { size?: number }) => JSX.Element>> = {
  roofline: IconRoofline,
  garland: IconSwag,
  columns: IconColumn,
  palms: IconTree,
  trees: IconTree,
  other: IconBush,
  wreaths: IconWreath,
  clusters: IconCluster,
  accessories: IconBow,
}

/** Bushes and shrubs, which is what the reference calls the catch all. */
const LABEL: Partial<Record<LightCategory, string>> = {
  other: 'Bushes & shrubs',
  clusters: 'Hanging clusters',
}

export default function CategoryRail({
  onPick,
  onClose,
}: {
  onPick: () => void
  onClose?: () => void
}) {
  const category = useEditor((s) => s.category)
  const setCategory = useEditor((s) => s.setCategory)

  return (
    <aside className="categories" aria-label="What to add">
      <div className="cat-head">
        <div>
          <h2>Lighting</h2>
          <p>Pick what you are putting on this house.</p>
        </div>
        {onClose && (
          <button className="cat-close" onClick={onClose} aria-label="Close the list">
            &times;
          </button>
        )}
      </div>

      <div className="cat-grid">
        {GROUPS.map((g) => (
          <div key={g.label} className="cat-run">
            <p className="cat-group">{g.label}</p>
            {g.ids.map((id) => {
              const setup = CATEGORY_SETUP[id]
              if (!setup) return null
              const Icon = ICONS[id]
              return (
                <button
                  key={id}
                  className="cat-item"
                  aria-current={category === id ? 'true' : undefined}
                  onClick={() => {
                    setCategory(id)
                    onPick()
                  }}
                >
                  {Icon && <Icon size={17} />}
                  <span>{LABEL[id] ?? setup.label}</span>
                </button>
              )
            })}
          </div>
        ))}
      </div>

      {/*
        The reference closes this panel with a line about what the tool does.
        Ours says the one thing that is true of every item above it.
      */}
      <p className="cat-foot">
        Everything you draw is measured, priced and carried through to the
        quote on its own line.
      </p>
    </aside>
  )
}
